import * as Sentry from '@sentry/react';
import type { ErrorEvent, EventHint } from '@sentry/react';

// Magic-link URLs carry a single-use sign-in code (oobCode) alongside the
// Firebase Web API key. Sentry's breadcrumbs integration monkey-patches the
// history methods and records the URL *before* the app strips those params, so
// without this the live credential lands in the breadcrumb buffer and ships
// with every event captured for the rest of the page session. Added 2026-08-01.
// The '#' in the prefix class matters: a credential sitting in a URL fragment
// would otherwise pass hasCredential() and then survive replace() untouched,
// leaving the redaction silently no-op.
const CREDENTIAL_PARAMS = /([?&#])(oobCode|apiKey)=[^&#]*/gi;

const hasCredential = (value: unknown): value is string =>
  typeof value === 'string' && (value.includes('oobCode=') || value.includes('apiKey='));

const redactUrl = (value: string): string => value.replace(CREDENTIAL_PARAMS, '$1$2=REDACTED');

// The two DOMException names observed escaping the Firebase SDK's IndexedDB code paths as
// unhandled rejections (see sentryBeforeSend). Kept to what was observed on purpose: a new
// name surfaces as a new issue instead of vanishing into this filter. Added 2026-09-10.
const IDB_REJECTION_NAMES = ['UnknownError', 'AbortError'];

/**
 * Sentry release label, read from the <meta> tag vite.config.ts stamps into every page.
 *
 * Read from HTML rather than a build-time `define` on purpose: inlining it put a clock
 * value inside the JS chunk, which moved app-*.js on every build and broke
 * scripts/deploy-proof.mjs's change detection. See BUILD_VERSION_META in vite.config.ts
 * for the full reasoning — including the ordering constraint against deploy-proof:86.
 *
 * `document` is guarded because initSentry() runs at module scope in index.tsx, which
 * ALSO executes in Node during the SSG pass. 'unknown' there is correct and harmless —
 * real events come from browsers.
 */
const buildVersion = (): string => {
  if (typeof document === 'undefined') return 'unknown';
  return document.querySelector('meta[name="app-build-version"]')?.getAttribute('content') || 'unknown';
};

/**
 * The beforeSend filter stack, exported so `src/lib/sentry.test.ts` can exercise every
 * suppression directly — no Sentry mock, no env or hostname stubbing. Order matters: each
 * block is an exact match at a class boundary with a dated comment, caveat and falsifier,
 * and the credential redaction runs last so it applies to whatever survives. Extracted
 * from the inline `Sentry.init` option on 2026-09-10 without changing any existing block.
 */
export function sentryBeforeSend(event: ErrorEvent, hint: EventHint): ErrorEvent | null {
  // Suppress failed fetch errors from the Firebase Installations SDK.
  // These occur for users behind network filters (e.g. GFW) that block
  // firebaseinstallations.googleapis.com, or on flaky mobile networks.
  // The endpoint only serves Firebase Performance Monitoring registration
  // — failure has no user-facing impact (SDK falls back to default config).
  // Stack frame filename check is not viable in beforeSend (source maps
  // resolve server-side only). Breadcrumb URL check used instead.
  // Message check accepts both Chrome's "Failed to fetch" and WebKit's
  // "Load failed" — same TypeError class, different per-browser phrasing.
  // Suppressed 2026-04-07, filter corrected 2026-04-21, WebKit phrasing
  // added 2026-06-22 to cover JAVASCRIPT-REACT-8.
  const error = hint?.originalException;
  // Note: in @sentry/react v10, event.breadcrumbs is Breadcrumb[] directly — no .values
  // wrapper. The prior code read .values off the array prototype (the iterator-generator
  // method), so Array.isArray(...) was always false and this filter was a silent no-op
  // from the v10 SDK upgrade onwards. Corrected 2026-05-12.
  const hasFirebaseInstallationsBreadcrumb =
    Array.isArray(event.breadcrumbs) &&
    event.breadcrumbs.some(
      b =>
        b.category === 'fetch' &&
        typeof b.data?.url === 'string' &&
        b.data.url.includes('firebaseinstallations.googleapis.com')
    );
  if (
    error instanceof TypeError &&
    typeof error.message === 'string' &&
    (error.message.includes('Failed to fetch') || error.message.includes('Load failed')) &&
    hasFirebaseInstallationsBreadcrumb
  ) {
    return null;
  }
  // Suppress reCAPTCHA double-render errors from Firebase App Check's
  // internal retry logic. After a 403 throttle (appCheck/initial-throttle),
  // the Firebase SDK's setTimeout handler attempts to re-render the
  // reCAPTCHA Enterprise widget into an already-occupied DOM element.
  // initializeAppCheck() is called once at module scope — this is not an
  // application-level double-init. Not actionable. Suppressed 2026-04-08.
  if (
    event.exception?.values?.some(v =>
      v.value?.includes('reCAPTCHA has already been rendered in this element')
    )
  ) {
    return null;
  }
  // Suppress IndexedDB transaction errors from the Firebase Installations
  // SDK. On iOS Safari, the SDK occasionally opens an IndexedDB transaction
  // at the exact moment the connection is closing (e.g. during page
  // navigation or BFCache restore), throwing InvalidStateError. The error
  // originates inside @firebase/installations and has no user-facing
  // impact — the installation ID is re-persisted on the next page load.
  // Not actionable from the application codebase. Suppressed 2026-05-24.
  if (
    event.exception?.values?.some(v =>
      v.type === 'InvalidStateError' &&
      typeof v.value === 'string' &&
      v.value.includes("Failed to execute 'transaction' on 'IDBDatabase'")
    )
  ) {
    return null;
  }
  // Suppress "no recaptcha" from Firebase App Check. Thrown at
  // @firebase/app-check index.esm.js:1105, inside the callback assigned to
  // script.onload by loadReCAPTCHAEnterpriseScript — that file sets onload
  // and never onerror, so the throw escapes straight to window.onerror.
  // No try/catch around initializeAppCheck, no .catch, and no ErrorBoundary
  // can intercept it, so there is nothing to fix in app code.
  // Caveat for whoever reads this next: it is a WEAK canary. It fires only
  // on the narrow branch where the loader ran but grecaptcha.enterprise came
  // back undefined; gstatic being blocked outright throws nothing at all and
  // just leaves a permanently pending promise. Do not read its absence as
  // "App Check is healthy". Exact match so it cannot swallow anything else.
  // Suppressed 2026-08-15 (JAVASCRIPT-REACT-C).
  if (event.exception?.values?.some(v => v.value === 'no recaptcha')) {
    return null;
  }
  // Suppress bare IndexedDB DOMExceptions that escape the Firebase SDK as unhandled
  // rejections. Three issues, three browsers, one class (JAVASCRIPT-REACT-F/-G/-H,
  // diagnosed 2026-09-10): the browser's IndexedDB backend failed underneath the SDK —
  // a Chromium backing-store failure mid-load (Edge/Windows), WebKit losing its IDB
  // server across a 29-minute iOS suspension, WebKit aborting an in-flight request at
  // page teardown — and a Firebase-internal floating promise rethrew the browser's raw
  // DOMException where nothing awaits it. Verified in the installed SDKs before trusting
  // the mechanism: @firebase/auth 1.11.1's IndexedDB persistence poll
  // (setInterval(async () => this._poll(), 800)) retries four times with no delay and
  // then throws request.error unwrapped; @firebase/performance 0.7.9 floats
  // installations.getId().then(...) with no rejection handler. The app never rejects a
  // bare DOMException itself: it has no AbortController, and every DOM API it calls is
  // caught by name at the call site (see useShareOrCopy). Zero user impact: pages are
  // prerendered, an unhandled rejection never reaches the ErrorBoundary, and the only
  // casualty is cross-tab sign-in propagation for that one tab. Not fixable app-side
  // short of changing the auth persistence medium for every signed-in user, which is
  // disproportionate for three events (firebase.ts explains why persistence is left to
  // the SDK to negotiate).
  // Shape is exact and matches @sentry/browser 10.47.0's DOMException branch: the
  // rejection reason is a DOMException (rendered as type 'Error', value
  // '<name>: <message>', tag DOMException.code, and NO frames because attachStacktrace
  // is off), the mechanism is the global unhandledrejection handler, and the name is one
  // of the two observed. A DOMException WITH frames is a synchronous throw and is not
  // covered on purpose. Deliberately KEPT when Firestore has logged
  // 'INTERNAL UNHANDLED ERROR' on the same page: that is Firestore's persistent cache
  // (firebase.ts, both databases) dying mid-session, which halts Firestore for the tab
  // and must stay visible. Caveat: a future app-side AbortController or IndexedDB call
  // left uncaught would be hidden by this too — catch by name at the call site.
  // Falsifier, armed by release tracking: any event of this shape at a release >= the
  // one shipping this means the filter is dead in production; any event carrying the
  // Firestore line that still arrives is the exemption working as designed.
  // Suppressed 2026-09-10 (JAVASCRIPT-REACT-F, -G, -H).
  const values = event.exception?.values ?? [];
  const isBareIdbRejection =
    typeof DOMException !== 'undefined' &&
    error instanceof DOMException &&
    IDB_REJECTION_NAMES.includes(error.name) &&
    values[0]?.mechanism?.type === 'auto.browser.global_handlers.onunhandledrejection' &&
    !values.some(v => v.stacktrace?.frames?.length);
  const firestoreLoggedInternalError =
    Array.isArray(event.breadcrumbs) &&
    event.breadcrumbs.some(
      b =>
        b.category === 'console' &&
        typeof b.message === 'string' &&
        b.message.includes('INTERNAL UNHANDLED ERROR')
    );
  if (isBareIdbRejection && !firestoreLoggedInternalError) {
    return null;
  }
  // Suppress the Google Search App's injected WKWebView bridge listener. GSA's in-app
  // browser registers an anonymous window 'unload' handler that dereferences
  // window.webkit.messageHandlers; by unload the native handlers are gone and
  // JavaScriptCore throws "undefined is not an object (evaluating
  // 'window.webkit.messageHandlers')". Captured only because Sentry's browserApiErrors
  // integration wraps EventTarget.addEventListener globally (mechanism
  // auto.browser.browserapierrors.addEventListener; extra.arguments shows the trusted
  // unload event). Nothing served by this origin contains that string — 0 hits in
  // source and in every served chunk, measured 2026-09-10 — so the bridge name is a
  // clean class boundary, and JavaScriptCore quotes the failing source text verbatim.
  // The wrapper re-throws unchanged and the user was leaving the page. Same family as
  // JAVASCRIPT-REACT-D (Meta IAB), different injector; denyUrls cannot catch it because
  // the injected handler carries no frame. Falsifier: any event with this value at a
  // release >= the one shipping this means the filter is dead.
  // Suppressed 2026-09-10 (JAVASCRIPT-REACT-E).
  if (
    values.some(
      v =>
        v.type === 'TypeError' &&
        typeof v.value === 'string' &&
        v.value.includes('window.webkit.messageHandlers')
    )
  ) {
    return null;
  }
  // Redact sign-in credentials from the event's own request URL.
  // Breadcrumbs are already scrubbed at capture time by beforeBreadcrumb.
  const requestUrl = event.request?.url;
  if (event.request && hasCredential(requestUrl)) {
    event.request.url = redactUrl(requestUrl);
  }
  return event;
}

export function initSentry(): void {
  if (import.meta.env.DEV) return;
  // Skip when the production build is loaded from a local host (e.g. `vite preview`,
  // Electron previews, Claude Desktop's built-in browser). Real users never hit these
  // origins, and referrer-restricted APIs (Firebase Installations) throw 403
  // PERMISSION_DENIED there — noise, not signal. Added 2026-07-03 (JAVASCRIPT-REACT-9).
  if (
    typeof window !== 'undefined' &&
    (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1')
  ) {
    return;
  }
  if (!import.meta.env.VITE_SENTRY_DSN) return;

  try {
    Sentry.init({
      dsn: import.meta.env.VITE_SENTRY_DSN,
      environment: 'production',
      release: buildVersion(),
      tracesSampleRate: 0,
      // Drop errors thrown inside Meta in-app-browser INJECTED bundles (iabjs://
      // scheme — Instagram/Facebook Android WebView telemetry). Nothing loaded by
      // our origin can carry an iabjs:// filename, so the scheme is a clean class
      // boundary. First instance: "Error invoking postMessage: Java object is gone"
      // (JAVASCRIPT-REACT-D) — Instagram's navigation_performance_logger_android
      // flushing metrics to a destroyed Android JavascriptInterface bridge on
      // beforeunload; captured only because Sentry's browserApiErrors patch wraps
      // EventTarget.addEventListener globally. Zero app impact: the wrapper
      // re-throws unchanged and the user was leaving the page.
      // Verified in @sentry/core 10.47.0 before trusting the mechanism: the default
      // EventFilters integration matches denyUrls against the RAW runtime filename
      // of the root exception's LAST frame — the THROW SITE, because
      // stripSentryFramesAndReverse reverses frames and strips sentryWrapped — so
      // this cannot silently no-op on our own wrapper frame. Injected schemes have
      // no source maps, so the 2026-04-21 filename caveat below does not apply.
      // Runs as an event processor BEFORE beforeSend; the filters below never see
      // denied events. Accepted caveat: a throw inside a Meta shim that OUR code
      // invoked would also drop (Meta-IAB sessions only, not fixable app-side).
      // Falsifier, armed by release tracking: any REACT-D event at a release >= the
      // one shipping this proves the filter is not matching in production.
      // Suppressed 2026-08-31 (JAVASCRIPT-REACT-D).
      denyUrls: [/^iabjs:\/\//],
      // Tag whether this page came from the soft-404 fallback shell
      // (dist/notfound.html) or from a real prerendered page. The two are
      // byte-indistinguishable from the client, and a fallback hit always
      // produces a React #418 hydration mismatch — which is deliberately NOT
      // filtered, since it is also how a genuine hydration bug surfaces. This
      // tag is what separates them, and lets the fallback-hit URL distribution
      // be read off Sentry directly. Marker is injected by
      // scripts/generate-notfound.mjs. Added 2026-08-15.
      initialScope: {
        tags: {
          served_from:
            typeof window !== 'undefined' &&
            (window as unknown as { __FALLBACK_SHELL__?: boolean }).__FALLBACK_SHELL__
              ? 'fallback-shell'
              : 'prerendered',
        },
      },
      beforeBreadcrumb(breadcrumb) {
        // Redact sign-in credentials from navigation (from/to) and network (url)
        // breadcrumbs at capture time, before they enter the scope buffer.
        const data = breadcrumb.data;
        if (data) {
          for (const key of ['url', 'from', 'to'] as const) {
            const value = data[key];
            if (hasCredential(value)) data[key] = redactUrl(value);
          }
        }
        if (hasCredential(breadcrumb.message)) {
          breadcrumb.message = redactUrl(breadcrumb.message);
        }
        return breadcrumb;
      },
      // The filter stack lives in sentryBeforeSend (module scope) so it is testable.
      beforeSend: sentryBeforeSend,
    });
  } catch (e) {
    console.warn('Sentry failed to initialise:', e);
  }
}
